import { Field } from 'formik';
import { useState } from 'react';

import { PreferredUnitSystem } from '@frontend/api';
import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Tooltip,
  Text,
  VStack,
  OutlineCircleInfoIcon,
} from '@frontend/components';

import { UNITS_BY_SYSTEM_AND_MEASURE } from 'constants/unitOfMeasure';

import { NestingDirectionField } from './NestingDirectionField';
import { MeasuredUnitFormControl } from './NumberedFormControl';
import { PostOrPrefixInput } from './PostOrPrefixInput';
import type {
  FormFieldProps,
  LinearNestingDefaultsData,
  SheetNestingDefaultsData,
} from './types';

type NestSettingsProps = {
  isSubmitting: boolean;
  isLinearNest?: boolean;
  initialPartToPartClearance: string;
  initialFeedWindow?: string;
  unitSystem: PreferredUnitSystem;
};
export const NestSettings = ({
  isSubmitting,
  isLinearNest,
  initialPartToPartClearance,
  initialFeedWindow = '',
  unitSystem,
}: NestSettingsProps) => {
  const [currentPartToPartClearance, setCurrentPartToPartClearance] = useState(
    initialPartToPartClearance,
  );
  const [currentFeedWindow, setCurrentFeedWindow] = useState(initialFeedWindow);
  const unitLabels = UNITS_BY_SYSTEM_AND_MEASURE[unitSystem];
  if (isLinearNest) {
    return (
      <Box>
        <Text fontWeight={600} marginBottom={1.5} marginTop={1.5}>
          Nest Settings
        </Text>
        <Flex gap={2} marginBottom={2}>
          <FormControl isReadOnly={isSubmitting}>
            <FormLabel>Nesting Behavior</FormLabel>
            <VStack align="flex-start">
              <Field name="nestBehaviorCommonLine">
                {({
                  field,
                  form,
                }: FormFieldProps<LinearNestingDefaultsData, string>) => (
                  <Checkbox
                    {...field}
                    isChecked={form.values?.nestBehaviorCommonLine}
                    onChange={(e) => {
                      if (e.target.checked) {
                        form.setFieldValue('partToPartClearance', '0');
                      } else {
                        form.setFieldValue(
                          'partToPartClearance',
                          currentPartToPartClearance,
                        );
                      }
                      field.onChange(e);
                    }}
                    name="nestBehaviorCommonLine"
                    aria-label="Common Line Nesting Checkbox"
                  >
                    Common line
                  </Checkbox>
                )}
              </Field>
              <Field name="nestBehaviorUseFeedWindow">
                {({
                  field,
                  form,
                }: FormFieldProps<LinearNestingDefaultsData, string>) => (
                  <Checkbox
                    {...field}
                    isChecked={form.values?.nestBehaviorUseFeedWindow}
                    onChange={(e) => {
                      field.onChange(e);
                      if (!e.target.checked) {
                        form.setFieldValue('feedWindow', '');
                      } else if (e.target.checked) {
                        form.setFieldValue('feedWindow', currentFeedWindow);
                      }
                    }}
                    name="nestBehaviorUseFeedWindow"
                    aria-label="Use Feed Window Checkbox"
                  >
                    Use feed window
                  </Checkbox>
                )}
              </Field>
            </VStack>
          </FormControl>
          <Field name="partToPartClearance">
            {({
              field,
              form,
            }: FormFieldProps<LinearNestingDefaultsData, string>) => {
              return (
                <Tooltip
                  isDisabled={!form.values?.nestBehaviorCommonLine}
                  hasArrow={true}
                  label={
                    'You cannot have a part to part clearance with common line cutting.'
                  }
                  placement={'bottom'}
                >
                  <FormControl
                    isReadOnly={isSubmitting}
                    isDisabled={form.values?.nestBehaviorCommonLine}
                  >
                    <FormLabel>Part-to-part clearance</FormLabel>
                    <MeasuredUnitFormControl
                      icon2={<PostOrPrefixInput value={unitLabels.length} />}
                      field={field}
                      form={{
                        ...form,
                        setFieldValue: async (
                          fieldName,
                          value,
                          shouldValidate,
                        ) => {
                          const result = await form.setFieldValue(
                            fieldName,
                            value,
                            shouldValidate,
                          );
                          setCurrentPartToPartClearance(value);
                          return result;
                        },
                      }}
                      ariaLabel="Part-to-Part Clearance Nesting Value"
                      unitSystem={unitSystem}
                    />
                  </FormControl>
                </Tooltip>
              );
            }}
          </Field>
        </Flex>
        <Flex gap={2} marginBottom={2}>
          <Field name="cuttingToolWidth">
            {({
              field,
              form,
            }: FormFieldProps<LinearNestingDefaultsData, string>) => {
              return (
                <FormControl isReadOnly={isSubmitting}>
                  <FormLabel>Laser/Saw Width</FormLabel>
                  <MeasuredUnitFormControl
                    icon2={<PostOrPrefixInput value={unitLabels.length} />}
                    field={field}
                    form={form}
                    ariaLabel="Laser Saw Width Nesting Value"
                    unitSystem={unitSystem}
                  />
                </FormControl>
              );
            }}
          </Field>
          <Field name="feedWindow">
            {({
              field,
              form,
            }: FormFieldProps<LinearNestingDefaultsData, string>) => {
              return (
                <FormControl
                  isReadOnly={isSubmitting}
                  isDisabled={!form.values?.nestBehaviorUseFeedWindow}
                >
                  <Flex gap={0.25}>
                    <FormLabel>Feed window</FormLabel>
                    <Tooltip
                      variant="expanded"
                      label='The length at the end of a piece in which no parts should be cut in their entirety. Uncheck "Use feed window" to ignore.'
                    >
                      <OutlineCircleInfoIcon
                        tabIndex={0}
                        boxSize={2.5}
                        color="feature.secondary"
                      />
                    </Tooltip>
                  </Flex>
                  <MeasuredUnitFormControl
                    icon2={<PostOrPrefixInput value={unitLabels.length} />}
                    field={field}
                    form={{
                      ...form,
                      setFieldValue: async (
                        fieldName,
                        value,
                        shouldValidate,
                      ) => {
                        const result = await form.setFieldValue(
                          fieldName,
                          value,
                          shouldValidate,
                        );
                        setCurrentFeedWindow(value);
                        return result;
                      },
                    }}
                    ariaLabel="Feed Window Nesting Value"
                    unitSystem={unitSystem}
                  />
                </FormControl>
              );
            }}
          </Field>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <Text fontWeight={600} marginBottom={1.5} marginTop={1.5}>
        Nest Settings
      </Text>
      <Flex gap={2} marginBottom={2}>
        <FormControl isReadOnly={isSubmitting}>
          <FormLabel>Nesting Behavior</FormLabel>
          <VStack align="flex-start">
            <Field name="nestBehaviorCommonLine">
              {({
                field,
                form,
              }: FormFieldProps<SheetNestingDefaultsData, string>) => (
                <Checkbox
                  {...field}
                  isChecked={form.values?.nestBehaviorCommonLine}
                  onChange={(e) => {
                    if (e.target.checked) {
                      form.setFieldValue('partToPartClearance', '0');
                    } else {
                      form.setFieldValue(
                        'partToPartClearance',
                        currentPartToPartClearance,
                      );
                    }
                    field.onChange(e);
                  }}
                  name="nestBehaviorCommonLine"
                  aria-label="Common Line Nesting Checkbox"
                >
                  Common line
                </Checkbox>
              )}
            </Field>
            <Field name="nestBehaviorPartInPart">
              {({
                field,
                form,
              }: FormFieldProps<SheetNestingDefaultsData, string>) => (
                <Checkbox
                  {...field}
                  isChecked={form.values?.nestBehaviorPartInPart}
                  name="nestBehaviorPartInPart"
                  aria-label="Part-in-Part Nesting Checkbox"
                >
                  Part-in-part
                </Checkbox>
              )}
            </Field>
          </VStack>
        </FormControl>
        <Field name="nestingDirection">
          {({
            field,
            form,
          }: FormFieldProps<SheetNestingDefaultsData, string>) => (
            <NestingDirectionField
              field={field}
              form={form}
              isSubmitting={isSubmitting}
            />
          )}
        </Field>
      </Flex>
      <Flex gap={2} marginBottom={2}>
        <Field name="edgeBuffer">
          {({
            field,
            form,
          }: FormFieldProps<SheetNestingDefaultsData, string>) => {
            return (
              <FormControl isReadOnly={isSubmitting}>
                <FormLabel>Edge Buffer</FormLabel>
                <MeasuredUnitFormControl
                  icon2={<PostOrPrefixInput value={unitLabels.length} />}
                  field={field}
                  form={form}
                  ariaLabel="Edge Buffer Nesting Value"
                  unitSystem={unitSystem}
                />
              </FormControl>
            );
          }}
        </Field>
        <Field name="partToPartClearance">
          {({
            field,
            form,
          }: FormFieldProps<SheetNestingDefaultsData, string>) => {
            return (
              <Tooltip
                isDisabled={!form.values?.nestBehaviorCommonLine}
                hasArrow={true}
                label={
                  'You cannot have a part to part clearance with common line cutting.'
                }
                placement={'bottom'}
              >
                <FormControl
                  isReadOnly={isSubmitting}
                  isDisabled={form.values?.nestBehaviorCommonLine}
                >
                  <FormLabel>Part-to-part clearance</FormLabel>
                  <MeasuredUnitFormControl
                    icon2={<PostOrPrefixInput value={unitLabels.length} />}
                    field={field}
                    form={{
                      ...form,
                      setFieldValue: async (
                        fieldName,
                        value,
                        shouldValidate,
                      ) => {
                        const result = await form.setFieldValue(
                          fieldName,
                          value,
                          shouldValidate,
                        );
                        setCurrentPartToPartClearance(value);
                        return result;
                      },
                    }}
                    ariaLabel="Part-to-Part Clearance Nesting Value"
                    unitSystem={unitSystem}
                  />
                </FormControl>
              </Tooltip>
            );
          }}
        </Field>
      </Flex>
      <Flex gap={2} marginBottom={2}>
        <Field name="kerfWidth">
          {({
            field,
            form,
          }: FormFieldProps<SheetNestingDefaultsData, string>) => {
            return (
              <FormControl isReadOnly={isSubmitting} width={'calc(50% - 8px)'}>
                <FormLabel>Kerf Width</FormLabel>
                <MeasuredUnitFormControl
                  icon2={<PostOrPrefixInput value={unitLabels.length} />}
                  field={field}
                  form={form}
                  ariaLabel="Kerf Width Nesting Value"
                  unitSystem={unitSystem}
                />
              </FormControl>
            );
          }}
        </Field>
      </Flex>
    </Box>
  );
};
